import { Spin } from 'antd';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/lib/useAuth';

/**
 * Route guard.
 *
 * This is a usability control, not a security one. It stops an unauthenticated
 * visitor seeing an empty broken page; it does not protect data. Every request
 * the page makes is independently authorised server-side, and field masking is
 * applied before a response leaves (02 §4.1) — so bypassing this guard in the
 * browser gains an attacker nothing.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { identity, initialising } = useAuth();
  const location = useLocation();

  // Wait for the initial refresh to settle, or a page reload would bounce a
  // signed-in user to the login screen for a frame.
  if (initialising) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!identity) {
    // Remember where they were going so sign-in can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
